import { API_METHODS } from '@compass/client/constants/api-methods';
import { useStore } from '@store/index';

import { useFeatureFlagState } from '@/hooks/useFeatureFlag';
import { usePermissionMap } from '@/hooks/usePermissions';
import { webhooksFeatureFlagKey } from '@/pages/webhooks/utils';

const M = API_METHODS.WebhookService;

/** Per-action permissions for the Webhooks section, evaluated at organization scope. */
export interface WebhookPermissions {
  canListEndpoints: boolean;
  canGetEndpoint: boolean;
  canCreateEndpoint: boolean;
  canUpdateEndpoint: boolean;
  canDeleteEndpoint: boolean;
  canPauseEndpoint: boolean;
  canResumeEndpoint: boolean;
  canDisableEndpoint: boolean;
  canRotateSecret: boolean;
  canSendTestEvent: boolean;
  canReadHealth: boolean;
  canListDeliveries: boolean;
  canGetDelivery: boolean;
  canRetryDelivery: boolean;
  canBulkRetry: boolean;
  canGetOperation: boolean;
}

interface ShouldShowWebhooksResult {
  shouldShow: boolean;
  isLoading: boolean;
  organizationId: string | null;
  permissions: WebhookPermissions;
}

/**
 * Whether the Webhooks section is visible, plus every per-action permission.
 *
 * Two gates, both UI only (the API enforces `webhook` / `webhook_delivery`
 * itself):
 *   1. the per-org feature flag `webhooks_<org_id>` (dynconfig `feature_flag`);
 *   2. the server permission map at ORGANIZATION scope (programId null). Only
 *      Organization Admins hold the webhook resources (via all:all:organization),
 *      so in practice only they see the section.
 *
 * Callers must gate every query and mutation on these booleans: a 403 never
 * settles in the dashboard's transport (api.config.ts), so firing a denied
 * request leaves the UI loading forever.
 */
export const useShouldShowWebhooksPage = (contextOverride?: {
  organizationId?: string | null;
}): ShouldShowWebhooksResult => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const organizationId =
    contextOverride && 'organizationId' in contextOverride
      ? (contextOverride.organizationId ?? null)
      : (selectedOrganizationId ?? null);

  const flag = useFeatureFlagState(webhooksFeatureFlagKey(organizationId));
  const { data, isLoading: permissionsLoading } = usePermissionMap({
    organizationId,
    programId: null,
  });

  const perms = data?.permissions;
  // Fail closed: nothing is permitted until the flag is on and the map has loaded.
  const allowed = (method: string) => flag.enabled && perms?.[method] === true;

  const permissions: WebhookPermissions = {
    canListEndpoints: allowed(M.ListWebhookEndpoints),
    canGetEndpoint: allowed(M.GetWebhookEndpoint),
    canCreateEndpoint: allowed(M.CreateWebhookEndpoint),
    canUpdateEndpoint: allowed(M.UpdateWebhookEndpoint),
    canDeleteEndpoint: allowed(M.DeleteWebhookEndpoint),
    canPauseEndpoint: allowed(M.PauseWebhookEndpoint),
    canResumeEndpoint: allowed(M.ResumeWebhookEndpoint),
    canDisableEndpoint: allowed(M.DisableWebhookEndpoint),
    canRotateSecret: allowed(M.RotateWebhookEndpointSecret),
    canSendTestEvent: allowed(M.SendWebhookTestEvent),
    canReadHealth: allowed(M.GetWebhookEndpointHealth),
    canListDeliveries: allowed(M.ListWebhookDeliveries),
    canGetDelivery: allowed(M.GetWebhookDelivery),
    canRetryDelivery: allowed(M.RetryWebhookDelivery),
    canBulkRetry: allowed(M.BulkRetryWebhookDeliveries),
    canGetOperation: allowed(M.GetWebhookOperation),
  };

  return {
    // The section is the endpoints list; deliveries without endpoints is not a page.
    shouldShow: permissions.canListEndpoints,
    isLoading: flag.isLoading || permissionsLoading,
    organizationId,
    permissions,
  };
};
